import {
  useGraphApi,
  useListGraphCategories,
  useGraphEndpoint,
  useListGraphEntities,
  useGraphEnum,
  useGraphObject,
  useGraphWebhook,
} from '@squareup/dex-feature-hooks-graph';
import { useSelectMobileNavState } from '@squareup/dex-store-access-shared-main-selectors';
import { useSelectSpecVersionInView } from '@squareup/dex-tech-store-access-selectors';
import {
  ReferenceAPIDetailNavigation,
  ReferenceNavigation,
  ReferenceObjectNavigation,
} from '@squareup/dex-tech-ui';
import { versionInfoToVersionPath } from '@squareup/dex-tech-utils-routing';
import { NullableClassName } from '@squareup/dex-types-shared-ui';
import { TrackedBox } from '@squareup/dex-ui-user-interaction-publisher';
import {
  filterForValidGraphEntityObjects,
  filterForValidGraphEnums,
  isWebhookObject,
} from '@squareup/dex-utils-oas';
import clsx from 'clsx';
import React, { MutableRefObject, ReactNode, useMemo, useRef } from 'react';

import styles from './reference-navigation-container.module.css';

const ReferenceNavigationContainer = React.memo(
  ({ className, pathname }: NullableClassName & { pathname: string }) => {
    const asPathRef: MutableRefObject<string> = useRef(pathname);
    const result: MutableRefObject<ReactNode | null> = useRef(null);

    const { api } = useGraphApi();
    const { endpoint } = useGraphEndpoint();
    const { webhook } = useGraphWebhook();
    const { categories } = useListGraphCategories();
    const { object } = useGraphObject();
    const { enum: enumValue } = useGraphEnum();
    const { entities } = useListGraphEntities();

    const objectEntities = useMemo(() => {
      return filterForValidGraphEntityObjects(entities).filter(
        (obj) => !isWebhookObject(obj)
      );
    }, [entities]);

    const enumEntities = useMemo(() => {
      return filterForValidGraphEnums(entities);
    }, [entities]);

    const { versionInfo, error: versionInfoError } =
      useSelectSpecVersionInView();
    const { isOpen: isMobileNavOpen } = useSelectMobileNavState();
    const isMobileNavOpenRef: MutableRefObject<boolean> =
      useRef(isMobileNavOpen);

    if (!versionInfo) {
      return null;
    }

    if (versionInfoError) {
      return null;
    }

    const activeVersion = versionInfoToVersionPath(versionInfo);

    if (!activeVersion) {
      return null;
    }

    let trackingId = 'unknown';

    // Entity change is not enough to swap navigation. We also must have a path name change.
    // This optimizes out needless rerenders on navigation change as the enities and path change on separate cadences.
    if (
      asPathRef.current !== pathname ||
      result.current === null ||
      isMobileNavOpenRef.current === null ||
      isMobileNavOpenRef.current !== isMobileNavOpen
    ) {
      asPathRef.current = pathname;
      isMobileNavOpenRef.current = isMobileNavOpen;

      // Use a precedence of selection. The deepest visited item in the graph wins
      // For example the endpoint should override the api as the active item.
      // Considering this is a global context its known what the hierachy of the url is.
      if (endpoint) {
        trackingId = 'reference-endpoint';
        result.current = (
          <ReferenceAPIDetailNavigation
            activeVersion={activeVersion}
            activeItem={endpoint}
            isWebhookNav={false}
            isDeprecatedNav={endpoint.isDeprecated}
            isMobileNavOpen={isMobileNavOpen}
          />
        );
      } else if (webhook) {
        trackingId = 'reference-webhook';
        result.current = (
          <ReferenceAPIDetailNavigation
            activeVersion={activeVersion}
            activeItem={webhook}
            isWebhookNav={true}
            isDeprecatedNav={webhook.isDeprecated}
            isMobileNavOpen={isMobileNavOpen}
          />
        );
      } else if (object) {
        trackingId = 'reference-object';
        result.current = (
          <ReferenceObjectNavigation
            activeVersion={activeVersion}
            activeItem={object}
            isObjectNav={true}
            entities={objectEntities}
            isMobileNavOpen={isMobileNavOpen}
          />
        );
      } else if (enumValue) {
        trackingId = 'reference-enum';
        result.current = (
          <ReferenceObjectNavigation
            activeVersion={activeVersion}
            activeItem={enumValue}
            isObjectNav={false}
            entities={enumEntities}
            isMobileNavOpen={isMobileNavOpen}
          />
        );
      } else {
        trackingId = 'reference';
        result.current = (
          <ReferenceNavigation
            activeVersion={activeVersion}
            activeItem={api}
            categories={categories}
            isMobileNavOpen={isMobileNavOpen}
            pathname={pathname}
          />
        );
      }
    }

    return (
      <TrackedBox
        trackingId={trackingId}
        className={clsx(styles.container, className)}
      >
        {result.current}
      </TrackedBox>
    );
  }
);

ReferenceNavigationContainer.displayName = 'ReferenceNavigationContainer';

export { ReferenceNavigationContainer };
